/**
 * Vorlage „Auspressen“.
 *
 * Eine halbe Zitrone oder Orange wird mit der Schnittfläche auf den Kegel der
 * Zitruspresse gedrückt und hin und her gedreht. Saft tropft in die Schale
 * und steigt an.
 */
import { ease, fract, lerp, segment, wave } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { ellipsePath, path } from '../paths.ts';
import { circle, compact, faded, group, line, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { falling } from '../parts/effects.ts';
import { arcArrow, counter, GROUND_Y, shadow } from '../parts/stage.ts';
import { bowl, contents } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Schale der Presse und Kegel. */
const DISH = { cx: 112, width: 150, height: 30 } as const;
const RIM_Y = GROUND_Y - DISH.height;
const CONE = { base: RIM_Y + 10, tip: RIM_Y - 26, half: 24 } as const;

/** Zeitplan. */
const TIME = { down: [0.04, 0.16], press: [0.16, 0.72], up: [0.72, 0.84], reset: [0.88, 1] } as const;

/** Früchte und ihre Größe. */
const FRUITS = { lemon: 'Zitrone', orange: 'Orange', lime: 'Limette' } as const;
type Fruit = keyof typeof FRUITS;
const FRUIT_SIZE: Readonly<Record<Fruit, { half: number; height: number }>> = {
  lemon: { half: 30, height: 28 },
  orange: { half: 36, height: 32 },
  lime: { half: 26, height: 24 },
};

/** Kegel mit Rippen. */
function cone(): SceneNode[] {
  const outline = path()
    .move(DISH.cx - CONE.half, CONE.base)
    .curve(DISH.cx - CONE.half * 0.6, CONE.base - 10, DISH.cx - 6, CONE.tip + 4, DISH.cx, CONE.tip)
    .curve(
      DISH.cx + 6,
      CONE.tip + 4,
      DISH.cx + CONE.half * 0.6,
      CONE.base - 10,
      DISH.cx + CONE.half,
      CONE.base,
    )
    .close()
    .toString();
  const ribs = [-0.6, -0.25, 0.1, 0.45].map((f) =>
    line(DISH.cx + f * 4, CONE.tip + 6, DISH.cx + f * CONE.half, CONE.base, styles.fine('shade')),
  );
  return [
    shape(outline, styles.solid('paper')),
    group(ribs, { clip: outline }),
    shape(outline, styles.outline()),
  ];
}

/**
 * Halbe Frucht, Schnittfläche unten auf baseY. Die Streifen der Schale
 * wandern mit turn und zeigen die Drehung.
 */
function fruitHalf(cx: number, baseY: number, fruit: Fruit, turn: number): SceneNode[] {
  const { half, height } = FRUIT_SIZE[fruit];
  const dome = path()
    .move(cx - half, baseY)
    .curve(cx - half, baseY - height * 1.2, cx + half, baseY - height * 1.2, cx + half, baseY)
    .close()
    .toString();
  const stripes: SceneNode[] = [];
  for (let index = 0; index < 6; index += 1) {
    const f = fract(index / 6 + turn) * 2 - 1;
    const x = cx + f * half * 0.9;
    stripes.push(
      shape(
        path()
          .move(x, baseY - 2)
          .quad(cx + f * half * 0.45, baseY - height * 0.9, cx + f * half * 0.15, baseY - height * 0.92)
          .toString(),
        styles.fine('deep'),
      ),
    );
  }
  return [
    shape(dome, styles.flat(fruit === 'lime' ? 'shade' : 'tint')),
    group(stripes, { clip: dome }),
    // Spitze der Zitrone
    fruit === 'lemon'
      ? shape(ellipsePath(cx, baseY - height * 0.92, 4, 3), styles.solid('tint'))
      : circle(cx, baseY - height * 0.9, 1.5, styles.flat('deep')),
    shape(dome, styles.outline()),
    // Fruchtfleisch als heller Rand an der Schnittfläche
    line(cx - half + 3, baseY - 1.5, cx + half - 3, baseY - 1.5, {
      stroke: 'paper',
      strokeWidth: STROKE.detail,
    }),
  ];
}

/** Vorlage „Auspressen“. */
export const prepSqueezeTemplate = defineTemplate({
  id: 'prep.squeeze',
  group: 'prep',
  title: 'Auspressen',
  description:
    'Die halbierte Frucht wird mit der Schnittfläche auf die Zitruspresse gedrückt und hin und her gedreht, bis der Saft herausgepresst ist.',
  durationSeconds: 5,
  keywords: [
    'auspressen',
    'ausgepresst',
    'auspresse',
    'zitruspresse',
    'saft einer zitrone',
    'saft einer orange',
    'saft einer limette',
    'zitronensaft',
    'pressen',
  ],
  stillTime: 0.45,
  parameters: [choice('fruit', 'Frucht', FRUITS, 'lemon')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/orange/.test(lower)) return { fruit: 'orange' };
    if (/limette/.test(lower)) return { fruit: 'lime' };
    if (/zitrone/.test(lower)) return { fruit: 'lemon' };
    return {};
  },
  scene(values, t) {
    const fruit = textValue(values, 'fruit', 'lemon') as Fruit;
    const size = FRUIT_SIZE[fruit];
    const dish = bowl(DISH.cx, RIM_Y, DISH.width, DISH.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const down = ease.inOutCubic(segment(t, ...TIME.down));
    const up = ease.inOutCubic(segment(t, ...TIME.up));
    const pressPhase = segment(t, ...TIME.press);
    const pressing = pressPhase > 0 && pressPhase < 1;
    // Die Frucht sitzt auf dem Kegel und drückt bei jeder Drehung etwas nach.
    const seat = CONE.tip + 16;
    const baseY = lerp(seat - 50, seat, down) - up * 50 + (pressing ? Math.abs(wave(pressPhase, 4)) * 3 : 0);
    const turn = pressing ? wave(pressPhase, 2) * 0.12 : 0;
    const visible = 1 - segment(t, TIME.up[1] - 0.04, TIME.up[1]);
    const appear = segment(t, 0, TIME.down[0] + 0.04);

    const juice = ease.inOutSine(pressPhase) * (1 - reset);
    const level = lerp(0.9, 0.55, juice);

    return compact([
      ...counter(),
      shadow(DISH.cx, DISH.width * 0.4),
      ...dish.back,
      juice > 0.01 ? contents(dish, level, 'tint') : null,
      ...dish.front,
      ...cone(),
      ...(pressing
        ? [
            ...falling(DISH.cx - size.half + 4, baseY, RIM_Y + 6, 6, 4, t, {
              seed: 2,
              size: 1.8,
              loops: 3,
              tone: 'shade',
            }),
            ...falling(DISH.cx + size.half - 4, baseY, RIM_Y + 6, 6, 4, t, {
              seed: 6,
              size: 1.8,
              loops: 3,
              tone: 'shade',
            }),
          ]
        : []),
      faded(Math.min(appear, visible), fruitHalf(DISH.cx, baseY, fruit, turn)),
    ]);
  },
  hints(values) {
    const size = FRUIT_SIZE[textValue(values, 'fruit', 'lemon') as Fruit];
    return [arcArrow(DISH.cx, CONE.tip + 16 - size.height * 0.5, size.half + 12, 10, 200, 340)];
  },
});
